<template>
  <div>
    <vetster-blog-post
      v-for="post in posts"
      :key="post.id"
      :mobile-apps="mobileApps"
      :post="post"
      :related-posts="relatedPosts"
      @render="setCurrentPost"
    />
    <div
      v-if="isLoading"
      class="py-standard justify-items-center"
    >
      <vetster-loading
        :is-loading="isLoading"
        size="size-16"
        class="[&>svg]:w-full [&>svg]:h-full"
      />
    </div>
  </div>
</template>

<script>
import map from 'lodash/map';
import take from 'lodash/take';
import uniq from 'lodash/uniq';

import VetsterBlogPost from '@/brand-components/Content/BlogPost/VetsterBlogPost';
import VetsterLoading from '@/brand-components/VetsterLoading';
import NextPageScrolling from '@/mixins/NextPageScrolling';

export default {
  name: 'VetsterBlogPostLoader',

  components: {
    VetsterBlogPost,
    VetsterLoading,
  },

  mixins: [NextPageScrolling],

  props: {
    concerns: {
      type: Array,
      default: null,
    },
    mobileApps: {
      type: Object,
      required: true,
    },
    postTitle: {
      type: String,
      required: true,
    },
    postType: {
      type: String,
      required: true,
    },
    startingSlug: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      currentPost: null,
      isLoading: false,
      lastPage: null,
      page: 1,
      posts: [],
      upcomingPosts: [],
    };
  },

  computed: {
    hasConcernsMigrated() {
      return this.$statsig.ready && this.$statsig.checkGate('booking_concerns_migration');
    },
    healthConcerns() {
      return uniq(this.healthConcernsMigrated);
    },
    healthConcernsMigrated() {
      // NewHealthConcern->value => legacy HealthConcern->slug
      const legacySlugs = {
        EYE_ISSUES: 'eye',
        DIARRHEA_AND_VOMITING: 'gastro',
        BEHAVIOUR_CONSULTS: 'behavior',
        GROOMING_ADVICE: 'other',
        FLEA_AND_TICK: 'flea-and-tick',
        DIET_SUPPLEMENTS: 'nutrition',
      };

      if (this.hasConcernsMigrated) {
        return this.concerns.map(
          (value) => Object.keys(legacySlugs).find((key) => legacySlugs[key] === value) ?? value,
        ).filter((value) => /^[A-Z_]+$/.test(value));
      }

      return this.concerns.map((value) => legacySlugs[value] ?? value)
        .filter((value) => /^[a-z-]+$/.test(value));
    },
    isLast() {
      return this.currentPost && this.indexOfSlug(this.currentPost) === this.posts.length - 1;
    },
    nextPost() {
      return this.upcomingPosts[0];
    },
    relatedPosts() {
      return take(this.upcomingPosts, 3);
    },
  },

  watch: {
    scrollPercentage(val) {
      if (val > 70 && !this.isLoading && this.isLast) {
        this.fetchNextPost();
      }
    },
  },

  async created() {
    this.isLoading = true;
    await this.fetchUpcomingPosts();
    await this.fetchNextPost();
    this.isLoading = false;
  },

  mounted() {
    this.observeScrollHeight();
    this.observePost(document.getElementById('bottom-section'), {
      title: this.postTitle,
      type: this.postType,
      slug: this.startingSlug,
    });
  },

  methods: {
    async fetchNextPost() {
      this.isLoading = true;

      if (!this.nextPost) {
        this.page += 1;
        if (this.page <= this.lastPage) {
          await this.fetchUpcomingPosts();
        }
      }

      if (this.nextPost) {
        try {
          const response = await this.$api.get(`p/posts/${this.nextPost.post_type.slug}/${this.nextPost.slug}`);
          const nextPost = response.data.data.data;
          this.posts.push(nextPost);
          this.upcomingPosts.shift();
        } catch (e) {
          //
        }
      }

      this.isLoading = false;
    },
    async fetchUpcomingPosts() {
      try {
        const response = await this.$api.get('p/blog-post', {
          params: {
            'health-concerns': this.healthConcerns,
            page: this.page,
            'post-type': this.postType,
            'slugs-excluded': [this.startingSlug, ...map(this.posts, 'slug')],
          },
        });
        this.upcomingPosts = response.data.data;
        this.lastPage = response.data.meta.last_page;
      } catch (e) {
        //
      }
    },
    indexOfSlug(slug) {
      return this.posts.findIndex((post) => post.slug === slug);
    },
    setCurrentPost(postSlug) {
      this.currentPost = postSlug;
    },
  },
};
</script>
